import gql from 'graphql-tag';

import {
  COUPON_FRAGMENT,
  CUSTOMER_FRAGMENT,
  DISCOUNT_ACTIVITY_FRAGMENT,
  FULL_DISCOUNT_PRESENT_FRAGMENT,
  PACKAGE_DISCOUNT_FRAGMENT,
  PURCHASE_PREMIUM_FRAGMENT,
  SELECTIVE_GIFT_ACTIVITY_FRAGMENT,
  SHOPPING_CREDITS_CLAIM_ACTIVITY_FRAGMENT,
  SHOPPING_CREDITS_DEDUCTION_ACTIVITY_FRAGMENT,
  USER_COUPON_FRAGMENT,
  MEMBER_FRAGMENT,
} from '../fragments';

/**
 * 获取优惠券礼包详情
 */
export const fetchGetCouponBundleDocument = gql`
  query couponBundle($id: ID!) {
    couponBundle(id: $id) {
      id
      createdAt
      updatedAt
      name
      remarks
      totalQuantity
      receivedQuantity
      state
      invalidCondition
      introduce
      smallProgramQRCodeLink
      couponBundleItems {
        id
        couponBundleId
        createdAt
        updatedAt
        quantity
        coupon {
          ...Coupon
        }
      }
      availableCoupon {
        quantity
        coupon {
          ...Coupon
        }
      }
    }
  }

  fragment Coupon on Coupon {
    __typename
    id
    createdAt
    state
    enable
    updatedAt
    name
    remarks
    type
    preferentialContent {
      preferentialType
      minimum
      discount
      maximumOffer
      includingDiscountProducts
    }
    validityPeriod {
      type
      startTime
      endTime
      numberOfDays
    }
    totalQuantity
    applicableProduct {
      applicableType
      productIds
    }
    whetherRestrictUsers
    memberPlanIds
    introduce
    promotion {
      id
    }
    memberPlanIds
    activityContent
    activityTime
  }
`;

/**
 * 领取优惠卷礼包
 */
export const fetchReceiveCouponBundleDocumenet = gql`
  mutation receiveCouponBundle($id: ID!) {
    receiveCouponBundle(id: $id) {
      ...UserCoupon
    }
  }
  ${USER_COUPON_FRAGMENT}
`;

/**
 * 获取用户优惠卷详情
 */
export const fetchUserCouponDocument = gql`
  query userCoupon($options: UserCouponListOptions, $userCouponId: ID!, $removeNewTag: Boolean) {
    userCoupon(options: $options, userCouponId: $userCouponId, removeNewTag: $removeNewTag) {
      ...UserCoupon
    }
  }
  ${USER_COUPON_FRAGMENT}
`;

/**
 * 获取用户全部优惠卷
 */
export const fetchUserCouponsDocumenet = gql`
  query userCoupons($options: UserCouponListOptions) {
    userCoupons(options: $options) {
      items {
        ...UserCoupon
      }
      totalItems
    }
  }
  ${USER_COUPON_FRAGMENT}
`;

/**
 * 获取用户优惠卷详情
 */
export const fetchUserCouponDocumenet = gql`
  query userCoupon($options: UserCouponListOptions, $userCouponId: ID!, $removeNewTag: Boolean) {
    userCoupon(options: $options, userCouponId: $userCouponId, removeNewTag: $removeNewTag) {
      ...UserCoupon
    }
  }
  ${USER_COUPON_FRAGMENT}
`;

/**
 * 领取优惠卷
 */
export const fetchClaimCouponDocumenet = gql`
  mutation claimCoupon($couponId: ID!) {
    claimCoupon(couponId: $couponId) {
      ...UserCoupon
    }
  }
  ${USER_COUPON_FRAGMENT}
`;

/**
 * 领取优惠券后订阅
 */
export const fetchCreateCouponExpiredNoticeSubscriptionRecordDocumenet = gql`
  mutation createCouponExpiredNoticeSubscriptionRecord($userCouponId: ID!) {
    createCouponExpiredNoticeSubscriptionRecord(userCouponId: $userCouponId)
  }
`;

/**
 * 获取优惠券的持有信息
 */
export const fetchGetCouponHoldDocument = gql`
  query getCouponHold($couponId: ID!) {
    getCouponHold(couponId: $couponId) {
      coupon {
        ...Coupon
      }
      usableCoupons {
        ...UserCoupon
      }
      expiredCoupons {
        ...UserCoupon
      }
      usedCoupons {
        ...UserCoupon
      }
      notStartedCoupons {
        ...UserCoupon
      }
      residualNumber
    }
  }
  ${COUPON_FRAGMENT}
  ${USER_COUPON_FRAGMENT}
`;

/**
 * 获取商品列表
 */
export const fetchProductListOptionsDocument = gql`
  query products($options: ProductListOptions) {
    products(options: $options) {
      items {
        id
        name
        enabled
        productTotalStock
        pointsExchangeInfo {
          id
          exchangeLimit
          allowPurchaseAtOriginalPrice
          exchangeConditionType
          pointExchange {
            points
            cash
            exchangeTotal
          }
        }
        productPurchasePermission {
          id
          productId
          isMembershipPlanPurchase
          membershipPlans {
            id
          }
          guideMembershipPlanId
        }
        memberPriceActivityAmount {
          minDiscount
          maxDiscount
          minMemberPriceAmount
          maxMemberPriceAmount
          memberPriceProductVariant {
            memberPriceAmount
            productVariantId
            memberDiscount
            discountType
          }
        }
        featuredAsset {
          source
        }
        customFields {
          price
          markingPrice
          limitType
          limitCount
          putOnSaleType
          putOnSaleTime
          isVipProduct
          isHiddenCart
          virtualTargetType
          productType
        }
        participatingActivities {
          id
          createdAt
          updatedAt
          endsAt
          couponCode
          perCustomerUsageLimit
          enabled
          customFields {
            type
            activityName
          }
        }
        offlineSales {
          offlineSales
        }
      }
      totalItems
    }
  }
`;

/**
 * 获取热门搜索列表
 */
export const fetchHotWordsDocument = gql`
  query hotWords($options: HotWordListOptions) {
    hotWords(options: $options) {
      items {
        id
        name
        createdAt
        updatedAt
        type
        imgUrl
        jumpType
        jumpValue
      }
      totalItems
    }
  }
`;

/**
 * 分销员点赚添加推广商品
 */
export const fetchAddDistributorShareProductDocumenet = gql`
  mutation addDistributorShareProduct($productId: ID!) {
    addDistributorShareProduct(productId: $productId) {
      id
    }
  }
`;

/**
 * 获取地址列表
 */
export const fetchActiveCustomerDocument = gql`
  query activeCustomer {
    activeCustomer {
      ...Customer
    }
  }
  ${CUSTOMER_FRAGMENT}
`;

/**
 * 获取地址省市区数据资源
 */
export const fetchRegionsJsonDocument = gql`
  query getRegionsJson($regionNames: [String]!) {
    getRegionsJson(regionNames: $regionNames) {
      value
      text
      children {
        value
        text
        children {
          value
          text
          children {
            value
            text
          }
        }
      }
    }
  }
`;

/**
 * 上传文件
 */
export const fetchUploadBystreamDocument = gql`
  mutation uploadBystream($input: FileInput!) {
    uploadBystream(input: $input)
  }
`;

/**
 * 获取模版id配置
 */
export const getSubscribeMessageTemplateIdDocument = gql`
  query getSubscribeMessageTemplateId {
    getSubscribeMessageTemplateId {
      couponGrants
      blindBoxActivityBooking
      shoppingCreditsNotification
      couponExpiredNotice
      wishBoxActivityBooking
    }
  }
`;

/**
 * 获取浮窗数据
 */
export const fetchFloatingWindowDocument = gql`
  query floatingWindow {
    floatingWindow {
      id
      isOpen
      width
      height
      isShowAfterReceived
      floatingWindowImage
      floatingWindowImageReceived
      jumpType
      jumpValue
    }
  }
`;

/**
 * 获取浮窗优惠券记录
 */
export const fetchGetFloatingWindowCouponRecordDocument = gql`
  query getFloatingWindowCouponRecord {
    getFloatingWindowCouponRecord
  }
`;

/**
 * 获取全局分享设置
 */
export const fetchShareSettingsDocument = gql`
  query shareSettings($pageKeys: [PageKey]) {
    shareSettings(pageKeys: $pageKeys) {
      id
      pageKey
      shareTitle
      shareImg
      channelId
    }
  }
`;

/**
 * 获取轮播数据
 */
export const fetchBannersDocument = gql`
  query banners($options: BannerListOptions) {
    banners(options: $options) {
      items {
        id
        image
        redirect
        redirectType
      }
      totalItems
    }
  }
`;

/**
 * 获取个人中心配置数据
 */
export const fetchPersonalCenterDocument = gql`
  query personalCenter {
    personalCenter {
      id
      topComponentType
      topComponent {
        backGroundImage {
          ...PictureResource
        }
        backGroundColor
        layoutType
        pictures {
          ...PictureResource
        }
        productGroup {
          menuCollection {
            menuName
            collectionId
          }
          groupType
        }
        productItems {
          showType
          ids
        }
        productCategoryList {
          banner {
            ...PictureResource
          }
          title
          subCategory {
            banner {
              ...PictureResource
            }
            title
          }
        }
        countdownEndTime
        backGroundColor
        fontColor
        borderColor
        borderInsideColor
      }
      bottomComponentType
      bottomComponent {
        backGroundImage {
          ...PictureResource
        }
        backGroundColor
        layoutType
        pictures {
          ...PictureResource
        }
        productGroup {
          menuCollection {
            menuName
            collectionId
          }
          groupType
        }
        productItems {
          showType
          ids
        }
        productCategoryList {
          banner {
            ...PictureResource
          }
          title
          subCategory {
            banner {
              ...PictureResource
            }
            title
          }
        }
        countdownEndTime
        backGroundColor
        fontColor
        borderColor
        borderInsideColor
      }
    }
  }
  fragment PictureResource on PictureResource {
    imgHeight
    imgWidth
    imgUrl
    videoUrl
    alt
    jump {
      ...Jump
    }
  }
  fragment Jump on Jump {
    startAxisX
    startAxisY
    endPointAxisX
    endPointAxisY
    jumpType
    jumpValue
    jumpName
  }
`;

export const fetchClickAdvertisingSaveDocument = gql`
  mutation clickAdvertisingSave($input: AdvertisingAttributionInput!) {
    clickAdvertisingSave(input: $input)
  }
`;

/**
 * 程序启动（热启动）上报
 */
export const fetchUserClickBuriedPointDocument = gql`
  mutation userClick {
    userClick
  }
`;

/**
 * 商品点击上报
 */
export const fetchUserClickProductBuriedPointDocument = gql`
  mutation userClickProduct($productId: ID) {
    userClickProduct(productId: $productId)
  }
`;

/**
 * 用户设备信息上报
 */
export const fetchUserDeviceDocument = gql`
  mutation userDevice($openIdKey: String!, $deviceInfo: JSON) {
    userDevice(openIdKey: $openIdKey, deviceInfo: $deviceInfo) {
      errorCode
      message
    }
  }
`;

/**
 * 分销信息上报
 */
export const fetchReportedDistributorRecordDocument = gql`
  mutation reportedDistributorRecord(
    $md5Str: String
    $sourceCode: String
    $distributionId: String
  ) {
    reportedDistributorRecord(
      md5Str: $md5Str
      sourceCode: $sourceCode
      distributionId: $distributionId
    )
  }
`;

/**
 * 来源信息上报
 */
export const fetchAddCustomerSourceDocument = gql`
  mutation addCustomerSource($customerSourceInput: CustomerSourceInput!) {
    addCustomerSource(customerSourceInput: $customerSourceInput) {
      id
    }
  }
`;

/**
 * 小程序来源信息上报
 */
export const fetchAddCustomerSourceMiniProgramDocument = gql`
  mutation addCustomerSourceMiniProgram($customerReferralInput: CustomerReferralSourceInput!) {
    addCustomerSourceMiniProgram(customerReferralInput: $customerReferralInput) {
      id
    }
  }
`;

/**
 * 获取店铺数据
 */
export const fetchActiveChannelDocument = gql`
  query customActiveChannel {
    customActiveChannel {
      id
      customFields {
        shopName
        shopLogo
        updateShippingAddressLimitTime
        amount
        points
        enablePickup
        globalLotteryActivityId
      }
    }
  }
`;

/**
 * 获取积分商品列表
 */
export const fetchGetPointsProductsDocument = gql`
  query getPointsProducts($isAllowExchange: Boolean, $options: PointsProductListOptions) {
    getPointsProducts(isAllowExchange: $isAllowExchange, options: $options) {
      items {
        allowPurchaseAtOriginalPrice
        exchangeConditionType
        pointExchange {
          points
          cash
          exchangeTotal
        }
        product {
          id
          name
          enabled
          productTotalStock
          featuredAsset {
            source
          }
          customFields {
            price
            markingPrice
            limitType
            limitCount
            putOnSaleType
            putOnSaleTime
            isVipProduct
            isHiddenCart
            virtualTargetType
          }
        }
      }
      totalItems
    }
  }
`;

/**
 * 商品对应的所有活动信息数据
 */
export const fetchProductActivitiesFindAllDocument = gql`
  query productActivitiesFindAll($productId: ID!) {
    productActivitiesFindAll(productId: $productId) {
      ... on Coupon {
        ...Coupon
      }
      ... on PurchasePremium {
        ...PurchasePremium
      }
      ... on DiscountActivity {
        ...DiscountActivity
      }
      ... on FullDiscountPresent {
        ...FullDiscountPresent
      }
      ... on PackageDiscount {
        ...PackageDiscount
      }
      ... on SelectiveGiftActivity {
        ...SelectiveGiftActivity
      }
      ... on ShoppingCreditsClaimActivity {
        ...ShoppingCreditsClaimActivity
      }
      ... on ShoppingCreditsDeductionActivity {
        ...ShoppingCreditsDeductionActivity
      }
    }
  }
  ${COUPON_FRAGMENT}
  ${PURCHASE_PREMIUM_FRAGMENT}
  ${DISCOUNT_ACTIVITY_FRAGMENT}
  ${FULL_DISCOUNT_PRESENT_FRAGMENT}
  ${PACKAGE_DISCOUNT_FRAGMENT}
  ${SELECTIVE_GIFT_ACTIVITY_FRAGMENT}
  ${SHOPPING_CREDITS_CLAIM_ACTIVITY_FRAGMENT}
  ${SHOPPING_CREDITS_DEDUCTION_ACTIVITY_FRAGMENT}
`;

/**
 * 添加赠品
 */
export const fetchSelectGiftDocument = gql`
  mutation selectGift(
    $orderId: ID!
    $skuId: ID!
    $giftId: ID!
    $promInstanceId: ID!
    $ladderLevel: Int
  ) {
    selectGift(
      orderId: $orderId
      skuId: $skuId
      giftId: $giftId
      promInstanceId: $promInstanceId
      ladderLevel: $ladderLevel
    ) {
      id
    }
  }
`;

/**
 * 删除全部订单项
 */
export const fetchRemoveAllOrderLinesByOrderTypeDocument = gql`
  mutation removeAllOrderLinesByOrderType($type: OrderPurchaseType!) {
    removeAllOrderLinesByOrderType(type: $type) {
      id
    }
  }
`;

/**
 * 删除赠品
 */
export const fetchCancelGiftDocument = gql`
  mutation cancelGift($orderId: ID!, $giftId: ID!, $promInstanceId: ID!) {
    cancelGift(orderId: $orderId, giftId: $giftId, promInstanceId: $promInstanceId) {
      id
    }
  }
`;

/**
 * 选择优惠换购商品
 */
export const fetchSelectMarkUpDocument = gql`
  mutation selectMarkUp($orderId: ID!, $skuId: ID!, $productId: ID!, $promInstanceId: ID!) {
    selectMarkUp(
      orderId: $orderId
      skuId: $skuId
      productId: $productId
      promInstanceId: $promInstanceId
    ) {
      id
    }
  }
`;

/**
 * 取消选择优惠换购商品
 */
export const fetchCancelMarkUpDocument = gql`
  mutation cancelMarkUp($orderId: ID!, $productId: ID!, $promInstanceId: ID!) {
    cancelMarkUp(orderId: $orderId, productId: $productId, promInstanceId: $promInstanceId) {
      id
    }
  }
`;

/**
 * 取消加价购全部商品
 */
export const fetchCancelAllMarkUpDocument = gql`
  mutation cancelAllMarkUp($orderId: ID!) {
    cancelAllMarkUp(orderId: $orderId) {
      id
    }
  }
`;

/**
 * 查询商品限购数量
 */
export const fetchGetPurchaseQuantityDocument = gql`
  query getPurchaseQuantity(
    $productId: ID!
    $type: OrderPurchaseType
    $isIncludeCurrentOrder: Boolean
  ) {
    getPurchaseQuantity(
      productId: $productId
      type: $type
      isIncludeCurrentOrder: $isIncludeCurrentOrder
    )
  }
`;

/**
 * 精选配置商品
 */
export const fetchSettingsDocument = gql`
  query settings($keyNames: [SettingKey]) {
    settings(keyNames: $keyNames) {
      id
      createdAt
      updatedAt
      key
      value
    }
  }
`;

/**
 * 获取商品规格
 */
export const fetchProdcutVariantsDocument = gql`
  query product($id: ID) {
    product(id: $id) {
      id
      productTotalStock
      optionGroups {
        id
        code
        name
        customFields {
          priority
        }
        options {
          id
          code
          name
          customFields {
            priority
          }
        }
      }
      variants {
        id
        name
        exclusionGroupsTypeName
        isCanPurchase
        options {
          id
          code
          name
          group {
            id
            code
            name
          }
        }
        customFields {
          virtualTargetType
        }
        virtualTarget {
          ... on Coupon {
            id
            cname: name
            cstate: state
            enable
            createdAt
            updatedAt
            remarks
            type
            preferentialContent {
              preferentialType
              minimum
              discount
              maximumOffer
              includingDiscountProducts
            }
            validityPeriod {
              type
              startTime
              endTime
              numberOfDays
            }
            totalQuantity
            applicableProduct {
              applicableType
              productIds
            }
            whetherRestrictUsers
            memberPlanIds
            introduce
            promotion {
              id
            }
            activityContent
            activityTime
          }
          ... on MembershipPlan {
            id
            name
            state
          }
        }
        featuredAsset {
          source
        }
        isThereAnyStock
        price
        sku
      }
    }
  }
`;

/**
 * 获取商品详情
 */
export const fetchProductDetailDocument = gql`
  query product($id: ID) {
    product(id: $id) {
      id
      name
      description
      enabled
      productTotalStock
      pointsExchangeInfo {
        id
        exchangeLimit
        allowPurchaseAtOriginalPrice
        exchangeConditionType
        pointExchange {
          points
          cash
          exchangeTotal
        }
      }
      productPurchasePermission {
        id
        productId
        isMembershipPlanPurchase
        membershipPlans {
          id
        }
        guideMembershipPlanId
      }
      memberPriceActivityAmount {
        minDiscount
        maxDiscount
        minMemberPriceAmount
        maxMemberPriceAmount
        memberPriceProductVariant {
          memberPriceAmount
          productVariantId
          memberDiscount
          discountType
        }
      }
      optionGroups {
        id
        code
        name
        customFields {
          priority
        }
        options {
          id
          code
          name
          customFields {
            priority
          }
        }
      }
      variants {
        id
        name
        exclusionGroupsTypeName
        isCanPurchase
        pointExchange {
          points
          cash
          exchangeTotal
        }
        customFields {
          virtualTargetType
        }
        virtualTarget {
          ... on Coupon {
            id
            cname: name
            cstate: state
            enable
            createdAt
            updatedAt
            remarks
            type
            preferentialContent {
              preferentialType
              minimum
              discount
              maximumOffer
              includingDiscountProducts
            }
            validityPeriod {
              type
              startTime
              endTime
              numberOfDays
            }
            totalQuantity
            applicableProduct {
              applicableType
              productIds
            }
            whetherRestrictUsers
            memberPlanIds
            introduce
            promotion {
              id
            }
            activityContent
            activityTime
          }
          ... on MembershipPlan {
            id
            name
            state
          }
        }
        options {
          code
          name
          id
          group {
            id
            code
            name
          }
        }
        featuredAsset {
          source
        }
        isThereAnyStock
        price
        sku
      }
      featuredAsset {
        source
      }
      assets {
        id
        width
        height
        name
        preview
        source
        type
      }
      customFields {
        price
        markingPrice
        isVipProduct
        unit
        particulars
        limitType
        limitCount
        productType
        virtualTargetType
        isHiddenCart
        subscriptionPlan {
          id
          periodAndDiscount {
            expireNumberOfPeriod
            discountType
            discountAmount
            recommend
          }
          subscriptionInterval {
            unit
            frequency
          }
        }
        putOnSaleType
        putOnSaleTime
        shareCover
      }
      participatingActivities {
        id
        createdAt
        updatedAt
        endsAt
        couponCode
        perCustomerUsageLimit
        enabled
        customFields {
          activityName
          type
        }
      }
      offlineSales {
        offlineSales
      }
    }
  }
`;

/**
 * 查询订单栏各待办数量
 */
export const fetchPendingOrdersNumberDocument = gql`
  query pendingOrdersNumber {
    pendingOrdersNumber {
      toBePaidNumber
      toBeShippedNumber
      toBeReceivedNumber
      toBeEvaluatedNumber
      toBeAfterSalesNumber
    }
  }
`;

/**
 * 获取商品列表 - 自定义排序
 */
export const fetchProductsSortByProductIdsDocument = gql`
  query productsSortByProductIds($options: ProductListOptions) {
    productsSortByProductIds(options: $options) {
      items {
        id
        name
        enabled
        productTotalStock
        pointsExchangeInfo {
          id
          exchangeLimit
          allowPurchaseAtOriginalPrice
          exchangeConditionType
          pointExchange {
            points
            cash
            exchangeTotal
          }
        }
        productPurchasePermission {
          id
          productId
          isMembershipPlanPurchase
          membershipPlans {
            id
          }
          guideMembershipPlanId
        }
        memberPriceActivityAmount {
          minDiscount
          maxDiscount
          minMemberPriceAmount
          maxMemberPriceAmount
          memberPriceProductVariant {
            memberPriceAmount
            productVariantId
            memberDiscount
            discountType
          }
        }
        featuredAsset {
          source
        }
        customFields {
          price
          markingPrice
          limitType
          limitCount
          putOnSaleType
          putOnSaleTime
          isVipProduct
          isHiddenCart
          virtualTargetType
        }
        participatingActivities {
          id
          createdAt
          updatedAt
          endsAt
          couponCode
          perCustomerUsageLimit
          enabled
          customFields {
            type
            activityName
          }
        }
        offlineSales {
          offlineSales
        }
      }
      totalItems
    }
  }
`;

/** 订单列表基础字段 */
const orderListItemFields = `
  id
  code
  state
  total
  isPaymentRewardActivity
  fulfillments {
    method
  }
  customFields {
    buyType
    subscription {
      id
      expireNumberOfPeriod
    }
    periods
    deliveryInterval
    timeoutPeriodToBePaid
    orderPromotionResult {
      promResult {
        orderTotalPrice
        orderTotalPoints
      }
    }
  }
  lines {
    id
    quantity
    totalQuantity
    unitPrice
    featuredAsset {
      preview
    }
    customFields {
      afterSaleLine {
        id
      }
    }
    productVariant {
      id
      sku
      price
      name
      options {
        code
        name
      }
      featuredAsset {
        source
      }
      customFields {
        virtualTargetType
      }
      product {
        id
        name
        enabled
        customFields {
          isVipProduct
        }
        featuredAsset {
          source
        }
        customFields {
          price
          markingPrice
          unit
        }
      }
    }
  }
`;

/** 订单自提信息字段 */
const orderListPickupFields = `
  orderPickup {
    deliveryType
    pickupCode
    status
    pickedUpAt
  }
`;

/**
 * 获取订单列表（包含自提信息）
 */
export const fetchOrderListWithPickupDocument = gql`
  query orders($options: OrderListOptions) {
    orders(options: $options) {
      items {
        ${orderListItemFields}
        ${orderListPickupFields}
      }
      totalItems
    }
  }
`;

/**
 * 获取订单列表（不包含自提信息）
 */
export const fetchOrderListWithoutPickupDocument = gql`
  query orders($options: OrderListOptions) {
    orders(options: $options) {
      items {
        ${orderListItemFields}
      }
      totalItems
    }
  }
`;

/**
 * 获取订单列表
 * @deprecated 请使用 fetchOrderListWithPickupDocument 或 fetchOrderListWithoutPickupDocument
 */
export const fetchOrderListDocument = fetchOrderListWithPickupDocument;

/**
 * 获取tabbar和功能页面配置信息
 */
export const fetchGetWeappDynamicRouteConfigDocument = gql`
  query getWeappDynamicRouteConfig {
    getWeappDynamicRouteConfig {
      tabbarConfig {
        text
        iconUrl
        selectedIconUrl
        routeValue
        path
        isBadge
      }
      eventTrackingConfig {
        eventScheme
        routeValue
      }
    }
  }
`;

/**
 * 激活会员
 */
export const fetchMembershipCardActivationDocument = gql`
  mutation membershipCardActivation($input: ActivateMembershipCardInput!) {
    membershipCardActivation(input: $input) {
      ...Member
    }
  }
  ${MEMBER_FRAGMENT}
`;

/**
 * 获取自提门店列表
 */
export const fetchPickupStoresDocument = gql`
  query pickupStores($latitude: Float, $longitude: Float) {
    pickupStores(latitude: $latitude, longitude: $longitude) {
      id
      createdAt
      updatedAt
      name
      code
      province
      city
      district
      address
      phone
      latitude
      longitude
      openTime
      closeTime
      pickupDateNote
      enabled
      distance
    }
  }
`;

/**
 * 扫码查询自提订单信息
 */
export const fetchPickupOrderByCodeDocument = gql`
  query pickupOrderByCode($pickupCode: String!) {
    pickupOrderByCode(pickupCode: $pickupCode) {
      order {
        id
        orderPlacedAt
        code
        state
        isPaymentRewardActivity
        orderBuyUserCouponId
        customFields {
          subscription {
            id
            expireNumberOfPeriod
          }
          buyType
          periods
          deliveryInterval
          isAvailableAfterSale
          timeoutPeriodToBePaid
          timeoutPeriodToBeReceived
          remark
          orderPromotionResult {
            promResult {
              orderTotalPrice
              orderTotalPoints
            }
          }
        }
        fulfillments {
          id
          createdAt
          method
        }
        lines {
          id
          quantity
          totalQuantity
          unitPrice
          featuredAsset {
            source
          }
          blindBoxRefundableAmount
          merchantVoluntaryRefund
          productVariant {
            id
            sku
            price
            name
            options {
              code
              name
            }
            featuredAsset {
              source
            }
            customFields {
              virtualTargetType
            }
            product {
              id
              name
              enabled
              customFields {
                isVipProduct
              }
              featuredAsset {
                source
              }
              customFields {
                price
                markingPrice
              }
            }
          }
          customFields {
            purchasePattern
            isAvailableAfterSale
            afterSaleLine {
              id
              createdAt
              updatedAt
              quantity
              price
            }
            afterSale {
              id
              cause
              state
              mode
            }
          }
        }
        surcharges {
          price
        }
        shipping
        shippingAddress {
          fullName
          company
          streetLine1
          streetLine2
          city
          province
          postalCode
          country
          countryCode
          phoneNumber
          customFields {
            district
          }
        }
      }
      canPickup
      cannotPickupReason
      orderPickup {
        deliveryType
        pickupCode
        status
        pickedUpAt
      }
      lines {
        orderLineId
        canPickup
        status
      }
    }
  }
`;

/**
 * 确认提货（分销员点击确认按钮后调用）
 */
export const fetchConfirmPickupDocument = gql`
  mutation confirmPickup($pickupCode: String!) {
    confirmPickup(pickupCode: $pickupCode) {
      id
      deliveryType
      pickupCode
      status
      pickedUpAt
    }
  }
`;

/**
 * 获取全部场景的自定义订阅通知配置
 */
export const fetchCustomNoticeSubscribeScenesDocument = gql`
  query customNoticeSubscribeScenes {
    customNoticeSubscribeScenes {
      scene
      enabled
      templates {
        id
        templateId
        title
      }
    }
  }
`;

/**
 * 授权自定义订阅通知后记录到数据库
 */
export const fetchUpsertCustomNoticeSubscriptionDocument = gql`
  mutation upsertCustomNoticeSubscription($noticeTemplateIds: [ID!]!) {
    upsertCustomNoticeSubscription(noticeTemplateIds: $noticeTemplateIds)
  }
`;
